/**
 * §11.2 connect-time disclosure. The copy is fixed and comes from @pm/shared; it
 * is shown in full at the moment of connecting, not linked to from terms, and the
 * acknowledgement is an explicit action rather than a pre-checked box.
 *
 * The secret is write-only: it is posted once and never read back by any
 * endpoint, so this form has no "current value" to display.
 */

import { useState } from 'react';
import {
  acceptedAuthTypes,
  credentialGuidance,
  CONNECT_ACCOUNT_DISCLOSURE,
  PLATFORM,
  PLATFORM_TRANSPORTS,
} from '@pm/shared';
import type { Transport } from '@pm/shared';
import { useMutation } from '../hooks/useApi';
import type { AuthType, Platform, SocialAccount } from '../api/types';
import { Card, Field } from './ui';
import { InlineError } from './ErrorView';
import { humanize } from '../lib/format';
import { routes } from '../api/routes';

export function ConnectAccountForm({
  botId,
  onConnected,
}: {
  botId: string;
  onConnected: (account: SocialAccount) => void;
}) {
  const [platform, setPlatform] = useState<Platform>('bluesky');
  const [transport, setTransport] = useState<Transport>('api');

  /*
   * The auth type is derived, not chosen. Offering all four on every platform is
   * how an owner handed X an `app_password` — a Bluesky concept X has never had
   * — and typed their account password into the box. Most pairs accept exactly
   * one shape, so the control only appears when there is a real choice.
   */
  const accepted = acceptedAuthTypes(platform, transport);
  const authType = accepted[0] ?? 'oauth2';
  const transports = PLATFORM_TRANSPORTS[platform];
  const [handle, setHandle] = useState('');
  const [secret, setSecret] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  const connect = useMutation<Record<string, unknown>, SocialAccount>(
    routes.bots.socialAccounts(botId),
  );

  return (
    <Card title="Connect an account">
      <section className="disclosure" aria-labelledby="connect-disclosure-heading">
        <h3 id="connect-disclosure-heading">{CONNECT_ACCOUNT_DISCLOSURE.heading}</h3>
        <ul>
          {CONNECT_ACCOUNT_DISCLOSURE.bullets.map((b) => (
            <li key={b}>{b}</li>
          ))}
        </ul>
      </section>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          const created = await connect.run({
            platform,
            transport,
            auth_type: authType,
            external_handle: handle,
            secret,
            scopes: [],
            acknowledged_disclosure: true,
          });
          if (created) {
            setSecret('');
            setAcknowledged(false);
            onConnected(created);
          }
        }}
      >
        <Field id="ca-platform" label="Platform">
          <select
            id="ca-platform"
            className="select"
            value={platform}
            onChange={(e) => {
              const next = e.target.value as Platform;
              setPlatform(next);
              // Carrying `browser` onto a platform that has no browser adapter
              // would submit a pair the server rejects.
              if (!PLATFORM_TRANSPORTS[next].includes(transport)) setTransport('api');
            }}
          >
            {PLATFORM.map((p) => (
              <option key={p} value={p}>
                {humanize(p)}
              </option>
            ))}
          </select>
        </Field>

        {transports.length > 1 ? (
          <Field
            id="ca-transport"
            label="How the platform is reached"
            hint="The official API where you hold developer access; a logged-in browser session otherwise."
          >
            <select
              id="ca-transport"
              className="select"
              value={transport}
              onChange={(e) => setTransport(e.target.value as Transport)}
            >
              {transports.map((t) => (
                <option key={t} value={t}>
                  {humanize(t)}
                </option>
              ))}
            </select>
          </Field>
        ) : null}

        <Field id="ca-handle" label="Handle on that platform">
          <input
            id="ca-handle"
            className="input"
            required
            value={handle}
            onChange={(e) => setHandle(e.target.value)}
          />
        </Field>

        {/* The guidance is per platform+transport because "secret" means
            something different on each, and an unlabelled box invites the most
            dangerous possible answer. */}
        <Field
          id="ca-secret"
          label={authType === 'app_password' ? 'App password' : 'Access token'}
          hint={`${credentialGuidance(platform, transport)} Stored encrypted and never shown again.`}
        >
          <input
            id="ca-secret"
            className="input"
            type="password"
            autoComplete="off"
            required
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
        </Field>

        <div className="field">
          <label htmlFor="ca-ack">
            <input
              id="ca-ack"
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />{' '}
            {CONNECT_ACCOUNT_DISCLOSURE.acknowledgeLabel}
          </label>
        </div>

        {connect.problem ? <InlineError problem={connect.problem} /> : null}

        <button
          type="submit"
          className="btn btn-primary"
          disabled={!acknowledged || connect.pending || !handle || !secret}
        >
          {connect.pending ? 'Connecting…' : 'Connect'}
        </button>
      </form>
    </Card>
  );
}
